import { useEffect, useCallback, useRef, useReducer, useMemo } from 'react';
import { derivedKeys, type OptionKey, type OptionMap } from './optionTypes';
import { getCachedOption, invalidateCachedOption, setCachedOption, subscribeToCacheChanges } from './optionsCache';
import { fetchSelectOptions as fetchAndCacheSelectOptions } from './fetchOptions';
import { useBackend } from '@/hooks/useBackend';
import { useAuthStore } from '@/stores/authStore';

/**
 * This hook is used to fetch select options from the backend and cache them locally
 * It takes an array of requested keys and returns the options, loading state, and error state
 * Any other components needing the same options will use the cached version
 *
 * While loading = true, the options will be empty, and components relying on this might not want to render yet.
 *
 * This avoids multiple requests for the same data - but stil fetches upon page load,
 * since the data could change, though not often.
 *
 */
export function useSelectOptions<T extends OptionKey>(requestedKeys: T[]) {
  const backend = useBackend();
  const currentUser = useAuthStore((state) => state.currentUser);
  const appAuthReady = useAuthStore((state) => state.isInitialized);

  // Callers often pass a fresh `['a','b']` literal each render. That used to recreate
  // `updateOptionsFromCache`, retrigger the main effect, dispatch `syncFromCache`, and loop.
  const keysFingerprint = [...requestedKeys]
    .map((k) => String(k))
    .sort((a, b) => a.localeCompare(b))
    .join('|');
  const canonicalRequestedKeys = useMemo(
    () => (keysFingerprint ? (keysFingerprint.split('|') as T[]) : []),
    [keysFingerprint]
  );
  // Note: OptionKey is designed to be equivalent to keyof OptionMap
  // We use a type assertion to bridge the gap between OptionKey and keyof OptionMap
  type OptionsType = Partial<Pick<OptionMap, T & keyof OptionMap>>;

  type SelectOptionsState = {
    options: OptionsType;
    loading: boolean;
    error: Error | null;
    isFetching: boolean;
    isUnauthenticated: boolean;
    isUnauthorized: boolean;
    isServerError: boolean;
    errorStatus: number | null;
    retryTrigger: number;
  };

  type SelectOptionsAction =
    | { type: 'retry' }
    | { type: 'authRecovered' }
    | { type: 'projectsAuthContextChanged' }
    | { type: 'setFetching'; value: boolean }
    | { type: 'syncFromCache'; options: OptionsType; hasAllOptions: boolean }
    | { type: 'setUnauthorized'; status: number }
    | { type: 'setUnauthenticated'; status: number }
    | { type: 'setServerError'; status: number }
    | { type: 'setGenericError'; error: Error };

  const initialState: SelectOptionsState = {
    options: {} as OptionsType,
    loading: true,
    error: null,
    isFetching: false,
    isUnauthenticated: false,
    isUnauthorized: false,
    isServerError: false,
    errorStatus: null,
    retryTrigger: 0,
  };

  const reducer = (state: SelectOptionsState, action: SelectOptionsAction): SelectOptionsState => {
    switch (action.type) {
      case 'retry':
        return {
          ...state,
          isUnauthenticated: false,
          isUnauthorized: false,
          isServerError: false,
          errorStatus: null,
          error: null,
          retryTrigger: state.retryTrigger + 1,
        };
      case 'authRecovered':
        return {
          ...state,
          isUnauthenticated: false,
          isUnauthorized: false,
          errorStatus: null,
          error: null,
          retryTrigger: state.retryTrigger + 1,
        };
      case 'projectsAuthContextChanged':
        return { ...state, retryTrigger: state.retryTrigger + 1 };
      case 'setFetching':
        return { ...state, isFetching: action.value, loading: action.value ? true : state.loading };
      case 'syncFromCache':
        return {
          ...state,
          options: action.options,
          loading: action.hasAllOptions ? state.isFetching : true,
        };
      case 'setUnauthorized':
        return {
          ...state,
          isUnauthorized: true,
          errorStatus: action.status,
          loading: false,
        };
      case 'setUnauthenticated':
        return {
          ...state,
          isUnauthenticated: true,
          errorStatus: action.status,
        };
      case 'setServerError':
        return {
          ...state,
          isServerError: true,
          errorStatus: action.status,
          loading: false,
        };
      case 'setGenericError':
        return { ...state, error: action.error };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { options, loading, error, isUnauthenticated, isUnauthorized, isServerError, errorStatus, retryTrigger } =
    state;

  // Manual retry function for errors
  const retry = useCallback(() => {
    dispatch({ type: 'retry' });
  }, []);

  // Reset error flags when authentication succeeds
  // This allows options to be fetched automatically after sign-in
  useEffect(() => {
    const shouldRecoverFromAuthError = (isUnauthenticated || isUnauthorized) && currentUser && !currentUser.isAnonymous;
    if (shouldRecoverFromAuthError) {
      dispatch({ type: 'authRecovered' });
    }
  }, [currentUser, isUnauthenticated, isUnauthorized]);

  /**
   * `backend.project.getAll()` (options key `projects`) varies with **authorization context**, not just
   * anonymous vs signed-in: role, sign-in-as, user id, etc. Invalidate when that context changes so we do
   * not keep a stale list (e.g. public-only) in the shared cache.
   *
   * **Ref + `prev === null`:** Each `useSelectOptions` caller has its own hook instance. Without this, the
   * effect’s first run on mount would invalidate and refetch every time another screen mounts that requests
   * `projects`, even though auth did not change—because we’d run `invalidateCachedOption` on every effect
   * execution. The ref stores the last `authContextKey` for *this* instance so we only invalidate when the
   * key actually changes after the initial baseline run (real login, role switch, sign-in-as, etc.).
   */
  // Intentional ref: keep prior auth key across renders without an extra state+effect; see block comment above.
  const prevProjectsAuthContextKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!(canonicalRequestedKeys as readonly OptionKey[]).includes('projects')) {
      return;
    }
    const authContextKey = [
      currentUser?.id ?? '',
      String(currentUser?.isAnonymous ?? true),
      String(currentUser?.selectedRole ?? ''),
      String(currentUser?.usingSignInAs ?? false),
    ].join('|');
    const prev = prevProjectsAuthContextKeyRef.current;
    prevProjectsAuthContextKeyRef.current = authContextKey;
    // First run for this hook instance: establish baseline only (see block comment above).
    if (prev === null) {
      return;
    }
    if (prev !== authContextKey) {
      invalidateCachedOption('projects');
      dispatch({ type: 'projectsAuthContextChanged' });
    }
  }, [
    canonicalRequestedKeys,
    currentUser?.id,
    currentUser?.isAnonymous,
    currentUser?.selectedRole,
    currentUser?.usingSignInAs,
  ]);

  // Function to update options from cache
  const updateOptionsFromCache = useCallback(() => {
    // Note: OptionKey is designed to be equivalent to keyof OptionMap
    type OptionsType = Partial<Pick<OptionMap, T & keyof OptionMap>>;
    const merged: OptionsType = {} as OptionsType;
    let hasAllOptions = true;

    for (const key of canonicalRequestedKeys) {
      const cached = getCachedOption(key);
      if (cached) {
        merged[key] = cached;
      } else {
        hasAllOptions = false;
      }
    }

    dispatch({ type: 'syncFromCache', options: merged, hasAllOptions });
  }, [canonicalRequestedKeys]);

  useEffect(() => {
    if (!backend || !appAuthReady) return;

    // Don't run if we already have an unauthenticated error (401)
    if (isUnauthenticated) {
      return;
    }

    // Don't run if we already have an unauthorized error (403) - wait for sign-in or manual retry
    if (isUnauthorized) {
      return;
    }

    // Don't run if we already have a server error (500) - wait for manual retry
    if (isServerError) {
      return;
    }

    let cancelled = false;

    // Subscribe to cache changes
    const unsubscribe = subscribeToCacheChanges(() => {
      if (!cancelled) {
        // Just update options from cache - derived options will be handled in the main effect
        updateOptionsFromCache();
      }
    });

    // Check if all requested keys are already cached
    const uncachedKeys = canonicalRequestedKeys.filter((k) => !getCachedOption(k)) as T[];

    // Based on those we need to load, check which are actually backend base vs derived.
    const allKeysToLoad: T[] = Array.from(
      new Set(new Set(uncachedKeys.flatMap((k) => (derivedKeys.has(k) ? derivedKeys.get(k)! : [k])) as T[]))
    );

    // Now load the uncached keys from the backend, and set them and the derived keys in the cache.
    const load = async () => {
      try {
        if (allKeysToLoad.length > 0) {
          dispatch({ type: 'setFetching', value: true });
          // Call the fetch function, which will cache everything
          // that is not already cached.
          await fetchAndCacheSelectOptions(backend, allKeysToLoad);
        }

        // Create derived options for any uncached derived keys
        for (const key of uncachedKeys.filter((k) => derivedKeys.has(k))) {
          const derived = createDerivedOptions(key);
          if (derived) {
            setCachedOption(key, derived as any);
          }
        }

        // Update options from cache after fetch completes
        if (!cancelled) {
          updateOptionsFromCache();
        }
      } catch (err) {
        if (!cancelled) {
          dispatch({ type: 'setGenericError', error: err as Error });

          // Check error status and categorize appropriately
          if (err && typeof err === 'object' && 'status' in err) {
            const status = (err as any).status as number;
            if (status === 401) {
              // 401 Unauthenticated - user needs to sign in
              dispatch({ type: 'setUnauthenticated', status });
              // Don't set loading to false here - let the error state persist
              // The auth system should handle redirecting to sign-in page
              return;
            } else if (status === 403) {
              // 403 Unauthorized - authenticated but insufficient permissions
              // Could be "anonymous access denied" (fixable by sign-in) or insufficient permissions
              dispatch({ type: 'setUnauthorized', status });
              // Stop automatic retries - user must sign in or manually retry
              return;
            } else if (status === 500) {
              // 500 Server Error - actual server problem
              dispatch({ type: 'setServerError', status });
              // Stop automatic retries - user must manually retry
              return;
            }
          }
        }
      } finally {
        if (!cancelled) {
          dispatch({ type: 'setFetching', value: false });
          // updateOptionsFromCache will handle setting loading to false
          updateOptionsFromCache();
        }
      }
    };

    // Initial update from cache
    updateOptionsFromCache();

    load();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [
    appAuthReady,
    backend,
    canonicalRequestedKeys,
    updateOptionsFromCache,
    isUnauthenticated,
    isUnauthorized,
    isServerError,
    retryTrigger,
  ]);

  // Separate effect to handle derived option recreation when base data becomes available
  useEffect(() => {
    if (!backend || !appAuthReady) return;

    // Don't run if we already have an unauthenticated error (401)
    if (isUnauthenticated) {
      return;
    }

    // Don't run if we already have an unauthorized error (403) - wait for sign-in or manual retry
    if (isUnauthorized) {
      return;
    }

    // Don't run if we already have a server error (500) - wait for manual retry
    if (isServerError) {
      return;
    }
    let cancelled = false;

    const checkAndRecreateDerivedOptions = () => {
      if (cancelled) return;

      const derivedKeysToRecreate = canonicalRequestedKeys.filter((k) => derivedKeys.has(k));
      for (const key of derivedKeysToRecreate) {
        const baseKeys = derivedKeys.get(key)!;
        const hasAllBaseData = baseKeys.every((baseKey) => getCachedOption(baseKey));
        if (hasAllBaseData) {
          const derived = createDerivedOptions(key);
          if (derived) {
            setCachedOption(key, derived as any);
          }
        }
      }
    };

    // Subscribe to cache changes for derived option recreation
    const unsubscribe = subscribeToCacheChanges(() => {
      if (!cancelled) {
        checkAndRecreateDerivedOptions();
      }
    });

    // Initial check
    checkAndRecreateDerivedOptions();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [appAuthReady, backend, canonicalRequestedKeys, isUnauthenticated, isUnauthorized, isServerError]);

  return {
    options,
    loading,
    error,
    isUnauthenticated,
    isUnauthorized,
    isServerError,
    errorStatus,
    retry,
  };
}

function createDerivedOptions(key: OptionKey) {
  switch (key) {
    case 'researcherOptions': {
      const staff = getCachedOption('staff');
      if (!staff) return null;
      return staff
        .filter((user) => user.faculty)
        .map((user) => ({
          key: user.id,
          value: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
        }));
    }
    case 'facultyOptions': {
      const staff = getCachedOption('staff');
      if (!staff) return null;
      return staff
        .filter((user) => user.roles?.some((r) => r.isFaculty))
        .map((user) => ({
          key: user.id,
          value: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
        }));
    }
    case 'researcherFacultyOptions': {
      const staff = getCachedOption('staff');
      if (!staff) return null;
      return staff
        .filter((user) => user.roles?.some((r) => r.isFaculty || r.isResearcher))
        .map((user) => ({
          key: user.id,
          value: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
        }));
    }
    case 'externalUserOptions': {
      const users = getCachedOption('users');
      if (!users) return null;
      return users.map((user) => ({
        key: user.id,
        value: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      }));
    }
    case 'researchGroupMap': {
      const staff = getCachedOption('staff');
      if (!staff) return null;
      const researchGroupMap = new Map<string, { key: string; value: string }[]>();
      staff.forEach((user) => {
        if (!user.id || !user.faculty) {
          return;
        }
        const researcher = { key: user.id, value: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() };
        if (!researchGroupMap.has(user.faculty)) {
          researchGroupMap.set(user.faculty, []);
        }
        const existingResearchers = researchGroupMap.get(user.faculty)!;
        if (!existingResearchers.some((r) => r.key === researcher.key)) {
          researchGroupMap.set(user.faculty, [...existingResearchers, researcher]);
        }
      });
      return researchGroupMap;
    }
    case 'userRoles': {
      const roles = getCachedOption('roles');
      if (!roles) return null;
      // Filter out system roles and project collaborator roles, keep only user roles
      // Based on the role structure, we want roles that are meant for users (not system roles)
      return roles
        .filter((role) => {
          // Keep roles that are user-related (not system roles or project-specific roles)
          // This filters out roles like system roles, project collaborator roles, etc.
          return role.id > 0 && role.isUser !== false;
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    case 'synthesisInstrumentOptions': {
      const instruments = getCachedOption('synthesisInstruments');
      if (!instruments) return null;
      return instruments
        .filter((inst) => inst.id !== undefined && inst.name !== undefined)
        .map((inst) => ({
          key: inst.id ?? '',
          value: `${inst.id ?? ''} - ${inst.name ?? 'Unknown Instrument'}`,
        }));
    }
    case 'characterizationInstrumentOptions': {
      const instruments = getCachedOption('characterizationInstruments');
      if (!instruments) return null;
      return instruments
        .filter((inst) => inst.id !== undefined && inst.name !== undefined)
        .map((inst) => ({
          key: inst.id ?? '',
          value: `${inst.id ?? ''} - ${inst.name ?? 'Unknown Instrument'}`,
        }));
    }
    case 'synthesisTechniquesLeafOnly': {
      const top = getCachedOption('synthesisTechniques');
      if (!top) return null;
      const list: any[] = [];
      top.forEach((parent: any) => {
        const hasChildren = (parent.subTechniques?.length ?? 0) > 0;
        if (parent.id != null && parent.name != null && !hasChildren) {
          list.push(parent);
        }
        (parent.subTechniques ?? []).forEach((t: any) => {
          if (t.id != null && t.name != null) list.push(t);
        });
      });
      return list;
    }
    case 'sampleMaterialOptions': {
      const materials = getCachedOption('sampleMaterials');
      if (!materials) return null;
      return materials
        .filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None')
        .sort((a, b) => a.name!.localeCompare(b.name!))
        .map((m) => ({
          id: m.id!,
          name: m.name!,
        }));
    }
    default:
      return null;
  }
}
